import React, {useState, useEffect, useCallback, useContext, useRef} from 'react';
import { withTranslation } from './ChildrenContext.js';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import Button from './Button';
import {I18nContext} from "../utils/I18n/context";

const CustomModal = ({
    visible,
    loading,
    confirmDisabled,
    className,
    bsSize,
    size,
    title,
    confirmName,
    areNoControls,
    showCancelButton,
    isForm,
    onSubmit,
    onClose,
    onCancel,
    onConfirm,
    children,
    t,
    disableHistoryTracking
}) => {
    const ctx = useContext(I18nContext) || {};
    const tModal = ctx.t?.modal ?? { defaultTitle: 'Modal' };
    const [state, setState] = useState({
        visible: visible,
        loading: loading
    });

    const historyStateRef = useRef({
        pushed: false,
        ignoreNextPopstate: false,
        modalId: null,
        originalState: null
    });
    const ignoreResetTimerRef = useRef(null);

    const scheduleIgnoreReset = useCallback(() => {
        if (ignoreResetTimerRef.current) {
            clearTimeout(ignoreResetTimerRef.current);
            ignoreResetTimerRef.current = null;
        }
        if (typeof window === 'undefined') {
            return;
        }
        ignoreResetTimerRef.current = setTimeout(() => {
            historyStateRef.current.ignoreNextPopstate = false;
            ignoreResetTimerRef.current = null;
        }, 0);
    }, []);

    const staticNames = (t && t.modal) || {};
    const staticActions = (t && t.actions) || {};

    useEffect(() => {
        setState(prevState => ({
            ...prevState,
            loading: loading
        }));
    }, [loading]);

    const close = useCallback(({ fromPopState } = {}) => {
        if (disableHistoryTracking) {
            onClose();
            return;
        }
        const canUseHistory = typeof window !== 'undefined' && window.history && typeof window.history.back === 'function';

        if (!fromPopState && canUseHistory && historyStateRef.current.pushed) {
            const originalState = historyStateRef.current.originalState;
            historyStateRef.current.ignoreNextPopstate = true;
            historyStateRef.current.pushed = false;
            historyStateRef.current.modalId = null;
            historyStateRef.current.originalState = null;

            if (typeof window.history.replaceState === 'function') {
                window.history.replaceState(originalState, '', window.location.href);
            }

            scheduleIgnoreReset();
            window.history.back();
        }

        onClose();
    }, [onClose, scheduleIgnoreReset]);

    const closeRef = useRef(close);
    useEffect(() => {
        closeRef.current = close;
    }, [close]);

    useEffect(() => {
        if (!visible || disableHistoryTracking) {
            return;
        }

        if (
            typeof window === 'undefined' ||
            !window.history ||
            typeof window.history.pushState !== 'function' ||
            typeof window.history.replaceState !== 'function'
        ) {
            return;
        }

        const originalState = window.history.state;
        const modalId = `modal-${Date.now()}-${Math.random().toString(36).substr(2)}`;
        historyStateRef.current.modalId = modalId;
        historyStateRef.current.originalState = originalState;
        historyStateRef.current.pushed = true;
        historyStateRef.current.ignoreNextPopstate = false;

        const trackedState = {
            modalTrackerId: modalId
        };

        window.history.replaceState(trackedState, '', window.location.href);
        window.history.pushState(trackedState, '', window.location.href);

        const handlePopState = (event) => {
            if (historyStateRef.current.ignoreNextPopstate) {
                historyStateRef.current.ignoreNextPopstate = false;
                return;
            }

            if (!historyStateRef.current.pushed) {
                return;
            }

            if (event.state && event.state.modalTrackerId === historyStateRef.current.modalId) {
                const storedState = historyStateRef.current.originalState;
                historyStateRef.current.pushed = false;
                historyStateRef.current.modalId = null;
                historyStateRef.current.originalState = null;

                if (typeof window.history.replaceState === 'function') {
                    window.history.replaceState(storedState, '', window.location.href);
                }

                closeRef.current({ fromPopState: true });
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);

            if (historyStateRef.current.pushed && typeof window !== 'undefined') {
                const storedState = historyStateRef.current.originalState;
                historyStateRef.current.pushed = false;
                historyStateRef.current.modalId = null;
                historyStateRef.current.originalState = null;

                if (typeof window.history.replaceState === 'function') {
                    window.history.replaceState(storedState, '', window.location.href);
                }

                if (!disableHistoryTracking) {
                    historyStateRef.current.ignoreNextPopstate = true;
                    scheduleIgnoreReset();
                    window.history.back();
                } else {
                    historyStateRef.current.ignoreNextPopstate = false;
                    if (ignoreResetTimerRef.current) {
                        clearTimeout(ignoreResetTimerRef.current);
                        ignoreResetTimerRef.current = null;
                    }
                }
            }

            historyStateRef.current.ignoreNextPopstate = false;
            historyStateRef.current.modalId = null;
            historyStateRef.current.originalState = null;

            if (ignoreResetTimerRef.current) {
                clearTimeout(ignoreResetTimerRef.current);
                ignoreResetTimerRef.current = null;
            }
        };
    }, [visible, scheduleIgnoreReset, disableHistoryTracking]);

    const confirm = useCallback(() => {
        if (state.loading || confirmDisabled) {
            return;
        }
        onConfirm();
    }, [state.loading, confirmDisabled, onConfirm]);

    return (
        <Modal
            show={visible}
            backdrop={'static'}
            size={bsSize || size}
            onHide={close}
            className={className}
            restoreFocus={false}
        >
            <form
                onSubmit={onSubmit ? onSubmit : null}
            >
                <Modal.Header closeButton={false}>
                    <button type="button" className="close" onClick={close}>
                        <span aria-hidden="true">×</span>
                        <span className="sr-only">{tModal.close || 'Close'}</span>
                    </button>
                    <h4 className="modal-title">{title || tModal.defaultTitle}</h4>
                </Modal.Header>
                <Modal.Body>
                    {children}
                </Modal.Body>
                {!areNoControls
                    ? (
                        <Modal.Footer>
                            {isForm
                                ? (
                                    <Button
                                        type={'submit'}
                                        extraClass={{ 'apply-btn': true }}
                                        loading={state.loading}
                                        disabled={state.loading || confirmDisabled}
                                        text={confirmName || staticActions.confirm}
                                    />
                                )
                                : (
                                    <Button
                                        extraClass={{ 'apply-btn': true }}
                                        loading={state.loading}
                                        disabled={state.loading || confirmDisabled}
                                        text={confirmName || staticActions.confirm}
                                        onClick={confirm}
                                    />
                                )
                            }
                            <Button
                                text={staticActions.cancel}
                                extraClass={{'cancel-btn': true}}
                                onClick={onCancel || close}
                            />
                        </Modal.Footer>
                    )
                    : null
                }
                {(areNoControls && showCancelButton) ?
                    <Modal.Footer>
                        <Button
                            text={staticActions.cancel}
                            extraClass={{'cancel-btn': true}}
                            onClick={onCancel || close}
                        />
                    </Modal.Footer>
                : ''}
            </form>
        </Modal>
    );
};

CustomModal.propTypes = {
    visible: PropTypes.bool,
    loading: PropTypes.bool,
    confirmDisabled: PropTypes.bool,
    className: PropTypes.string,
    bsSize: PropTypes.string,
    size: PropTypes.string,
    title: PropTypes.string,
    confirmName: PropTypes.string,
    areNoControls: PropTypes.bool,
    showCancelButton: PropTypes.bool,
    isForm: PropTypes.bool,
    onSubmit: PropTypes.func,
    onClose: PropTypes.func,
    onCancel: PropTypes.func,
    onConfirm: PropTypes.func,
    children: PropTypes.node,
    t: PropTypes.object,
    disableHistoryTracking: PropTypes.bool
};

CustomModal.defaultProps = {
    visible: false,
    loading: false,
    confirmDisabled: false,
    className: '',
    areNoControls: false,
    showCancelButton: false,
    isForm: false,
    onSubmit: null,
    onClose: () => {},
    onCancel: null,
    onConfirm: () => {},
    disableHistoryTracking: false
};

export default withTranslation(CustomModal);
